/**
 * "Add a note for the IT HelpDesk" (Rusty, 16 Oct): replaces "Ask Rusty a
 * question". A leader suggests a data point, an update, or something that
 * should be added to the report. Opened from the page header, and from under
 * an "Ask about this report" answer, where it starts with that question.
 *
 * The note lands in the admin Feedback inbox ("Leadership report" view) as a
 * note / suggestion, with the author, the date, the page's dates and filters
 * and the related question and answer, so Rusty can turn it into a backlog
 * item and resolve it like other feedback. No email; nothing comes back here.
 */

import { Alert, Button, Dialog, Field, Textarea, Typography } from "@norican/react";
import { useState } from "react";

import { useApiErrorMessage } from "@/components/common";
import { useT } from "@/i18n";

import { useAddReportNote } from "./api";
import { apiQuery, type InsightsState } from "./insights-helpers";
import { canSendComment, COMMENT_MAX, HELPDESK_OVERVIEW } from "./report-helpers";

/** What a note was written from: an "Ask about this report" question and its answer. */
export interface NoteContext {
  question: string;
  answer: string;
}

export interface NoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The page's range and filters (stored with the note). */
  state: InsightsState;
  /** The snapshot the viewer is reading, if any. */
  snapshotId: number | null;
  /** Set when the note is written under an "Ask about this report" answer. */
  context: NoteContext | null;
}

export function NoteDialog({ open, onOpenChange, state, snapshotId, context }: NoteDialogProps) {
  const { t } = useT("common");
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md">
          <Dialog.Header>
            <Dialog.Title>{t("leadershipReport.note.title")}</Dialog.Title>
            <Dialog.CloseButton label={t("button.close")} />
          </Dialog.Header>
          <Dialog.Body className="max-h-[70vh] overflow-y-auto">
            {open ? (
              <NoteForm
                key={context?.question ?? ""}
                state={state}
                snapshotId={snapshotId}
                context={context}
                onDone={() => onOpenChange(false)}
              />
            ) : null}
          </Dialog.Body>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function NoteForm({
  state,
  snapshotId,
  context,
  onDone,
}: {
  state: InsightsState;
  snapshotId: number | null;
  context: NoteContext | null;
  onDone: () => void;
}) {
  const { t } = useT("common");
  const errorMessage = useApiErrorMessage();
  const add = useAddReportNote(HELPDESK_OVERVIEW);
  const [note, setNote] = useState(
    context ? t("leadershipReport.note.prefill", { question: context.question }) : "",
  );
  const [saved, setSaved] = useState(false);
  const failure = add.isError ? errorMessage(add.error) : null;

  function send() {
    if (!canSendComment(note) || add.isPending) {
      return;
    }
    add.mutate(
      {
        ...apiQuery(state),
        note: note.trim(),
        snapshot_id: snapshotId,
        question: context?.question.slice(0, 1000) ?? null,
        answer: context?.answer.slice(0, 4000) ?? null,
      },
      {
        onSuccess: () => {
          setNote("");
          setSaved(true);
        },
      },
    );
  }

  if (saved) {
    return (
      <div className="flex flex-col gap-4">
        <Alert tone="success" title={t("leadershipReport.note.saved")} />
        <div>
          <Button size="sm" onClick={onDone}>
            {t("button.close")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Typography variant="body-sm" tone="muted">
        {t("leadershipReport.note.hint")}
      </Typography>
      {context ? (
        <div className="border-accent bg-bg-subtle flex flex-col gap-1 rounded-md border-l-4 px-3 py-2">
          <Typography variant="caption" tone="muted">
            {t("leadershipReport.note.about")}
          </Typography>
          <Typography variant="body-sm" className="break-words whitespace-pre-wrap">
            {context.question}
          </Typography>
        </div>
      ) : null}
      <Field name="leadership-report-note" label={t("leadershipReport.note.label")}>
        <Textarea
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={4}
          autoResize
          maxLength={COMMENT_MAX}
        />
      </Field>
      <Typography variant="caption" tone="muted">
        {t("leadershipReport.note.where")}
      </Typography>
      <div className="flex flex-wrap items-center gap-3">
        <Button disabled={!canSendComment(note) || add.isPending} onClick={send}>
          {add.isPending ? t("leadershipReport.note.saving") : t("leadershipReport.note.save")}
        </Button>
      </div>
      {failure ? (
        <Alert tone="danger" title={failure.title}>
          {failure.description}
          {failure.traceLabel ? ` ${failure.traceLabel}` : ""}
        </Alert>
      ) : null}
    </div>
  );
}
